/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable, reaction } from 'mobx';

import type { UserRoleRepresentation } from '@veeam-vspc/models/web-controllers';

import { PortalEnumsAddon } from '../../addons/portal-enums-addon';
import { PortalFeaturesAddon } from '../../addons/portal-features-addon';
import { PortalFiltersAddon } from '../../addons/portal-filters-addon';
import { PortalPreferencesAddon } from '../../addons/portal-preferences-addon';
import { NotificationDialogsAdapter } from '../../components/NotificationDialogs';
import {
    callCbWithArrayItemsInAsyncOrder,
    ConfiguratorService,
    ErrorNames,
    isErrorThatNeedToBeShown,
    LangService,
    ReturnUrlManagerService,
    SocketTransportService,
    TransportRequestTypes,
    TransportService,
    isFailedFetchError,
} from '../../core';
import { AppStates, ServerErrorCodes } from '../../enums';
import { AppHealth } from '../../helpers/app-health';
import { htmlDecode } from '../../helpers/string-helpers';
import { BaseLang } from '../../interfaces';
import { FileTransportService } from '../../services/file-transport';
import { LoggerService } from '../../services/logger';
import { NotificationService } from '../../services/notification';
import { PortalService } from '../../services/portal';
import { TokenManagerService } from '../../services/token-manager';
import { AppDataStore } from '../../stores/app-data';
import { CoreModuleAddons } from './interfaces';
import { canViewPortalPages, canCancelInterval } from './utils';

import type { PortalServiceConfig } from '../../services/portal';
import type { TokenManagerServiceConfig } from '../../services/token-manager';
import type { DefaultAppData } from '../../stores/app-data';
import type { AppRouteItem } from '../../stores/router';
import type { BaseCoreModuleConfig, CoreModuleContext, CoreModuleEventHandlers } from './interfaces';
import type { LoggerServiceConfig } from '../../services/logger';
import type { FileTransportServiceConfig } from '../../services/file-transport';
import type { AppPrefixes, AppServices, RequestErrorResponse } from '../../interfaces';
import type { AppStorage } from '../../helpers/storages';
import type { SharedStrategies } from '../../enums';
import type {
    ConfiguratorServiceConfig,
    HttpRequestService,
    LangServiceConfig,
    ReturnUrlManagerServiceConfig,
    ShowBackendErrorParams,
    SocketTransportServiceConfig,
    TransportCommands,
    TransportServiceConfig,
} from '../../core';
import type { BaseAddon, AddonContext, AddonsConfig } from '../../addons/base-addon';

export class BaseCoreModule<TransportFail> {

    strategy: SharedStrategies; // Should be defined in overrided class

    readonly prefixes: AppPrefixes;
    readonly getLoginPath: (returnUrl?: string) => string;
    readonly getPortalPath: () => string;
    readonly trustedUrlsWithNoError?: string[];
    readonly defaultAppData: DefaultAppData;
    readonly customCheckAuthorization?: (suppliers: Record<string, any>, services: AppServices) => Promise<void>;

    httpRequestService: HttpRequestService;

    transportService: TransportService<TransportFail>;
    transportServiceConfig: TransportServiceConfig<any, any, any>;

    tokenManagerService: TokenManagerService;
    tokenManagerServiceConfig: TokenManagerServiceConfig<TransportFail>;

    loggerService: LoggerService;
    loggerServiceConfig: LoggerServiceConfig;

    langService: LangService;
    langServiceConfig: LangServiceConfig;

    portalService: PortalService;
    portalServiceConfig: PortalServiceConfig<TransportFail>;

    notificationService: NotificationService;

    configuratorService: ConfiguratorService<UserRoleRepresentation>;
    configuratorServiceConfig: ConfiguratorServiceConfig<UserRoleRepresentation>;

    fileTransportService: FileTransportService;
    fileTransportServiceConfig: FileTransportServiceConfig<any, any, any>;

    socketTransportService: SocketTransportService;
    socketTransportServiceConfig: SocketTransportServiceConfig;

    returnUrlManagerService?: ReturnUrlManagerService<TransportFail>;
    returnUrlManagerServiceConfig?: ReturnUrlManagerServiceConfig<TransportFail>;

    @observable appState: AppStates = AppStates.Loading;

    @observable appDataStore: AppDataStore;

    @observable currentLang: BaseLang;
    @observable userRoutes: AppRouteItem[];

    @observable appStorage?: AppStorage | null = null;

    @observable addons: CoreModuleAddons = {};
    addonsConfig: AddonsConfig[];

    suppliers?: Record<string, any>;
    suppliersConfig?: Record<string, any>;

    appHealth: AppHealth;

    readonly appEventHandlers: CoreModuleEventHandlers;

    constructor(basePortalCoreConfig: BaseCoreModuleConfig) {
        makeObservable(this);

        const {
            httpRequestService,
            restPrefix,
            baseUrl,
            companyPrefix,
            productPrefix,
            trustedUrlsWithNoError,
            defaultAppData,
            getLoginPath,
            getPortalPath,
            transportConfig,
            tokenManagerConfig,
            loggerConfig,
            langConfig,
            portalConfig,
            configuratorConfig,
            returnUrlManagerConfig,
            fileTransportConfig,
            socketTransportConfig,
            suppliers,
            appEventHandlers,
            addonsConfig,
            customCheckAuthorization,
        } = basePortalCoreConfig;
        const transportCommands: TransportCommands = {
            getAuthToken: () => this.getAuthTokenCommand(),
            refreshToken: () => this.refreshTokenCommand(),
            setLastActiveTime: () => this.setLastActiveTimeCommand(),
            showAuthError: () => this.showAuthErrorCommand(),
            showBackendError: (errorResponse: RequestErrorResponse | Error | null, params: ShowBackendErrorParams) => (
                this.showBackendErrorCommand(errorResponse, params)
            ),
            log: (url: string, response: RequestErrorResponse | Response) => this.logCommand(url, response),
            ...transportConfig.commands,
        };

        this.prefixes = {
            companyPrefix,
            productPrefix,
        };
        this.getLoginPath = getLoginPath;
        this.getPortalPath = getPortalPath ?? (() => '');
        this.trustedUrlsWithNoError = trustedUrlsWithNoError;
        this.defaultAppData = defaultAppData;
        this.appEventHandlers = appEventHandlers ?? {};
        this.addonsConfig = addonsConfig ?? [];
        this.customCheckAuthorization = customCheckAuthorization;
        this.setAppDataStore(new AppDataStore(defaultAppData)); // default store
        this.langServiceConfig = langConfig;
        this.transportServiceConfig = {
            ...transportConfig,
            httpRequestService,
            restPrefix: transportConfig?.restPrefix ?? restPrefix,
            baseUrl: transportConfig?.baseUrl ?? baseUrl,
            commands: transportCommands,
        };
        this.tokenManagerServiceConfig = {
            ...tokenManagerConfig,
            httpRequestService,
            companyPrefix: tokenManagerConfig.companyPrefix ?? companyPrefix,
            restPrefix: tokenManagerConfig?.restPrefix ?? restPrefix,
            baseUrl: tokenManagerConfig?.baseUrl ?? baseUrl,
            commands: {
                showAuthError: () => this.showTokenResetError(),
                log: transportCommands.log,
                ...tokenManagerConfig.commands,
            },
        } as TokenManagerServiceConfig<TransportFail>; // transportService will add later
        this.loggerServiceConfig = loggerConfig as LoggerServiceConfig; // transportService will add later
        this.portalServiceConfig = portalConfig as PortalServiceConfig<TransportFail>; // transportService will add later
        this.configuratorServiceConfig = configuratorConfig;
        this.returnUrlManagerServiceConfig = returnUrlManagerConfig;
        this.fileTransportServiceConfig = {
            ...this.transportServiceConfig,
            ...fileTransportConfig,
            commands: {
                ...this.transportServiceConfig.commands,
                showStreamBackendError: (errorResponses: Array<RequestErrorResponse | Error>, params: ShowBackendErrorParams) => (
                    this.showStreamErrorCommand(errorResponses, params)
                ),
                ...fileTransportConfig?.commands,
            },
        };
        this.socketTransportServiceConfig = {
            commands: {
                getAuthToken: transportCommands.getAuthToken,
            },
            restPrefix: socketTransportConfig?.restPrefix ?? restPrefix,
            baseUrl: socketTransportConfig?.baseUrl ?? baseUrl,
        };
        this.suppliersConfig = suppliers;
    }

    createServices(): void {
        this.langService = new LangService(this.langServiceConfig);
        this.notificationService = new NotificationService(NotificationDialogsAdapter, {}, this.appDataStore.theme);
        this.transportService = new TransportService<TransportFail>(this.transportServiceConfig);
        this.tokenManagerService = new TokenManagerService({
            ...this.tokenManagerServiceConfig,
            transportService: this.transportService,
        });
        this.loggerService = new LoggerService({
            ...this.loggerServiceConfig,
            transportService: this.transportService,
        });
        this.portalService = new PortalService({
            ...this.portalServiceConfig,
            transportService: this.transportService,
        });
        this.configuratorService = new ConfiguratorService<UserRoleRepresentation>(this.configuratorServiceConfig);
        this.fileTransportService = new FileTransportService(this.fileTransportServiceConfig);

        if (this.returnUrlManagerServiceConfig) {
            this.returnUrlManagerService = new ReturnUrlManagerService<RequestErrorResponse | Error>(
                {
                    transportService: this.transportService,
                    notificationService: this.notificationService,
                    fileTransportService: this.fileTransportService,
                },
                this.returnUrlManagerServiceConfig,
            );
        }

        this.socketTransportService = new SocketTransportService<string>(this.socketTransportServiceConfig);
        this.initSuppliers(this.suppliers);

        this.initAppHealth();
    }

    @action.bound
    async logout(): Promise<void> {
        await this.resetAuthData();
        this.appEventHandlers.onLoggedOut?.(this.getCoreModuleContext());

        if (canViewPortalPages(this.appState)) {
            this.setAppState(AppStates.Loading);
        }
    }

    init(): Promise<void> {
        return Promise.resolve();
    }

    getServices(): AppServices {
        return {
            transportService: this.transportService,
            tokenManagerService: this.tokenManagerService,
            loggerService: this.loggerService,
            langService: this.langService,
            portalService: this.portalService,
            notificationService: this.notificationService,
            configuratorService: this.configuratorService,
            socketTransportService: this.socketTransportService,
            fileTransportService: this.fileTransportService,
            returnUrlManagerService: this.returnUrlManagerService,
        };
    }

    getSuppliers(): Record<string, any> {
        return this.suppliers ?? {};
    }

    @action.bound
    setAppDataStore(appDataStore: AppDataStore): void {
        this.appDataStore = appDataStore;
    }

    @action.bound
    setCurrentLang(lang: BaseLang): void {
        this.currentLang = lang;
    }

    @action.bound
    setUserRoutes(routes: AppRouteItem[]): void {
        this.userRoutes = routes;
    }

    @action.bound
    setAppStorage(appStorage: AppStorage | null): void {
        this.appStorage = appStorage;
    }

    @action.bound
    setAddons(addons: CoreModuleAddons): void {
        this.addons = addons;
    }

    @action.bound
    setAppState(state: AppStates): void {
        if (process.env.NODE_ENV === 'development') {
            console.log(`[DEV_MODE_MESSAGE] App state: "${state}"`);
        }
        this.appState = state;
    }

    getAuthTokenCommand(): Promise<string | null> {
        return this.tokenManagerService.getAccessToken();
    }

    refreshTokenCommand(): Promise<void> {
        return this.tokenManagerService.refreshToken();
    }

    setLastActiveTimeCommand(): void {
        this.tokenManagerService.setLastActiveTime();
    }

    @action
    resetLoadedData(): void {
        this.appDataStore.resetLoadableData();
        this.setAppStorage(null);
        Object.keys(this.addons ?? {}).forEach(key => this.addons[key].destroy());
        this.setAddons({});
    }

    @action
    async resetAuthData(): Promise<void> {
        await this.tokenManagerService.logout();
        this.appDataStore.logout();
        this.addons.portalPreferences?.stopPreferenceUpdater();
    }

    @action.bound
    showTokenResetError(): void {
        if (this.appState === AppStates.LoggedIn) {
            console.error('Session expired.');
            this.doOnSessionExpired();
        }
    }

    @action.bound
    showAuthErrorCommand(): void {
        if (this.appState === AppStates.LoggedIn) {
            console.error(`${ErrorNames.AuthInternalError} happened.`);
            this.doOnSessionExpired();
        }
    }

    @action.bound
    async doOnSessionExpired(): Promise<void> {
        // Do NOT reset here loaded data, because of page crush. Page continue stay alive on backdrop
        this.setAppState(AppStates.SessionExpired);
        await this.resetAuthData();
    }

    @action.bound
    doOnAppDestroyed(): void {
        // Do NOT reset data here, it will override error dialog
        this.setAppState(AppStates.Destroyed);
        this.addons.portalPreferences?.stopPreferenceUpdater();
    }

    showBackendErrorCommand(errorResponse: RequestErrorResponse | Error | null, params: ShowBackendErrorParams): void {
        const { url, title, type } = params;
        let messages = errorResponse && errorResponse instanceof Error
            ? [errorResponse.message]
            : errorResponse && (errorResponse as RequestErrorResponse).errors
                ? (errorResponse as RequestErrorResponse).errors.map(err => err.message)
                : [];
        const errorCodesForInfoIcon = [
            ServerErrorCodes.RetryLater,
        ];
        const shouldBeInfoIcon = errorResponse && errorResponse instanceof Error
            ? false
            : errorResponse && (errorResponse as RequestErrorResponse).errors
                ? (errorResponse as RequestErrorResponse).errors.some(err => errorCodesForInfoIcon.includes(err.code))
                : false;
        const defaultMessage = type === TransportRequestTypes.ToRestServer || type === TransportRequestTypes.ToWebServer
            ? this.currentLang.WEB_SERVER_IS_NOT_RESPONDING
            : this.currentLang.FAILED_TO_DOWNLOAD_LOG_FILES;

        if (
            this.trustedUrlsWithNoError
            && this.trustedUrlsWithNoError.find(tUrl => url.includes(tUrl)) !== undefined
        ) {
            // Decode HTML symbols if URL is trusted and safety of XSS attack
            messages = messages.map(message => htmlDecode(message)) as string[];
        }

        if (isFailedFetchError(messages?.[0] ?? '') && !canCancelInterval(this.appState)) {
            // Do NOT do any damage when appState became not ok
            this.appHealth.damage();

            if (process.env.NODE_ENV === 'development') {
                console.error(`[DEV_MODE_MESSAGE] App was damaged by "${messages?.[0]}".`);
            }
        } else if (!canCancelInterval(this.appState)) {
            // NOT show any errors when appState became not ok
            if (shouldBeInfoIcon && messages.length > 0) {
                this.notificationService.info(title ?? this.currentLang.INFORMATION, messages);
            } else {
                this.notificationService.error(
                    title ?? this.currentLang.ERROR,
                    messages.length > 0 ? messages : defaultMessage,
                );
            }
        } else {
            if (process.env.NODE_ENV === 'development') {
                const errorTitle = title ?? this.currentLang.ERROR;
                const message = messages.length > 0 ? messages : defaultMessage;
                console.error(`[DEV_MODE_MESSAGE] Error dialog with title "${errorTitle}" and message "${message}" was ignored by conditions.`);
            }
        }
    }

    showStreamErrorCommand(errorResponses: Array<RequestErrorResponse | Error>, params: ShowBackendErrorParams): void {
        const { title } = params;
        const messages = errorResponses.flatMap(errorResponse => errorResponse && errorResponse instanceof Error
            ? [errorResponse.message]
            : errorResponse && (errorResponse as RequestErrorResponse).errors
                ? (errorResponse as RequestErrorResponse).errors.map(err => err.message)
                : []);

        if (!canCancelInterval(this.appState)) {
            // NOT show any errors when appState became not ok
            this.notificationService.error(title ?? this.currentLang.ERROR, messages);
        } else {
            if (process.env.NODE_ENV === 'development') {
                const errorTitle = title ?? this.currentLang.ERROR;
                console.error(`[DEV_MODE_MESSAGE] Error dialog with title "${errorTitle}" and message "${messages}" was ignored by conditions.`);
            }
        }
    }

    logCommand(url: string, response: RequestErrorResponse | Error | Response): void {
        let errorMessage: string;

        if (response && response instanceof Error && isFailedFetchError(response.message)) {
            if (process.env.NODE_ENV === 'development') {
                console.error(`[DEV_MODE_MESSAGE] Logger ignore "${response.message}" error (via logCommand)`);
            }
            return;
        }

        if (response && typeof (response as Response).ok !== 'undefined') {
            errorMessage = `"${(response as Response).statusText}"`;
        } else if (response && typeof (response as Error).stack !== 'undefined') {
            errorMessage = `"${(response as Error).name}: ${(response as Error).message}"`;
        } else if (response && (response as Record<string, any>).errors) {
            errorMessage = `"${JSON.stringify((response as Record<string, any>).errors)}"`;
        } else {
            errorMessage = response as any;
        }

        const exceptionMessage = [
            `Message: ${errorMessage ? ` - ${errorMessage}` : ''}`,
            `Url: ${url}`,
        ].join('\r\n');

        if (process.env.NODE_ENV === 'development') {
            console.error(`[DEV_MODE_MESSAGE] Logger try to send exception "${exceptionMessage}" (via logCommand)`);
        }

        this.loggerService.write(exceptionMessage);
    }

    protected initSuppliers(suppliers: BaseCoreModuleConfig['suppliers'] = {}) {
        const supplierNames = Object.keys(suppliers);

        if (supplierNames.length > 0) {
            supplierNames.forEach((supplierName) => {
                this.suppliers[supplierName] = new suppliers[supplierName](this.transportService);
            });
        }
    }

    protected initAppHealth(suppliers: BaseCoreModuleConfig['suppliers'] = {}) {
        this.appHealth = new AppHealth();

        reaction(
            () => this.appHealth.hp,
            () => {
                if (this.appHealth.hp === 0) {
                    this.doOnAppDestroyed();
                }

                if (process.env.NODE_ENV === 'development') {
                    console.error(`[DEV_MODE_MESSAGE] ${this.appHealth.hp} hp left.`);
                }
            },
        );
    }

    protected loadLoginData(noAuth: boolean): Promise<AppDataStore> {
        return this.portalService.getLoginData({ noAuth })
            .then(appDataStoreParams => new AppDataStore(this.defaultAppData, appDataStoreParams))
            .catch((err) => {
                if (isErrorThatNeedToBeShown(err)) {
                    console.error(err);
                }

                return new AppDataStore(this.defaultAppData);
            });
    }

    protected loadAddonsData(noAuth: boolean): Promise<void> {
        const addonList = {
            portalEnums: PortalEnumsAddon,
            portalFeatures: PortalFeaturesAddon,
            portalFilters: PortalFiltersAddon,
            portalPreferences: PortalPreferencesAddon,
        };
        const {
            suppliers,
            services,
            appDataStore,
            appStorage,
            lang,
        } = this.getCoreModuleContext();
        const addonsContext: AddonContext = {
            suppliers,
            services,
            appDataStore,
            appStorage,
            lang,
            noAuth,
        };

        this.setAddons(Object.fromEntries(
            this.addonsConfig.map((addonNameOrConfig) => {
                if (typeof addonNameOrConfig === 'string') {
                    return [addonNameOrConfig, new addonList[addonNameOrConfig](addonsContext)];
                } else if (Array.isArray(addonNameOrConfig)) {
                    const [addonName, addonConfig, isAddonAllowed, initOrderNumber] = addonNameOrConfig;

                    if (
                        (typeof isAddonAllowed === 'function' && (isAddonAllowed as Function)(appDataStore))
                        || typeof isAddonAllowed !== 'function'
                    ) {
                        return [
                            addonName,
                            new addonList[addonName]({ ...addonsContext, initOrderNumber }, addonConfig),
                        ];
                    }

                    return null;
                }

                console.error('Wrong addons config.', addonNameOrConfig);
                return null;
            })
                .filter(item => item)
        ));

        const addonsByOrder = Object.keys(this.addons)
            .map(key => this.addons[key])
            .reduce((res, addon) => {
                const orderNumber = addon.getInitOrderNumber();

                if (Array.isArray(res[orderNumber])) {
                    res[orderNumber].push(addon);
                } else {
                    res[orderNumber] = [addon];
                }

                return res;
            }, [])
            .filter(item => Boolean(item));

        return callCbWithArrayItemsInAsyncOrder((addons: BaseAddon[]) => Promise.all(addons.map(addon => addon.init())), addonsByOrder);
    }

    protected getCoreModuleContext(): CoreModuleContext {
        return {
            suppliers: this.suppliers,
            services: this.getServices(),
            lang: this.currentLang,
            appDataStore: this.appDataStore,
            appStorage: this.appStorage,
            addons: this.addons,
        };
    }

}
